import React, { useState } from 'react';
import { Link } from 'react-router';
import { Workspace } from '../../lib/api/workspaces';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { Dropdown } from '../ui/dropdown/Dropdown';
import { DropdownItem } from '../ui/dropdown/DropdownItem';
import { cn } from '../../utils';

export const WorkspaceSelector: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { currentWorkspaceId, currentWorkspace, workspaces, loading, setWorkspace } = useWorkspace();

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const closeDropdown = () => {
    setIsOpen(false);
  };

  const handleWorkspaceSelect = (workspace: Workspace) => {
    setWorkspace(workspace.id);
    closeDropdown();
  };

  const handleAllWorkspacesSelect = () => {
    setWorkspace(null);
    closeDropdown();
  };

  const displayName = currentWorkspace?.name || 'All Workspaces';
  const totalFilings = workspaces.reduce((sum, w) => sum + (w.filing_count ?? 0), 0);

  return (
    <div className="relative inline-block w-full">
      <button
        onClick={toggleDropdown}
        className={cn(
          'dropdown-toggle inline-flex w-full items-center justify-between gap-2 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-dark dark:text-gray-300 dark:hover:bg-gray-800',
          loading && 'cursor-wait opacity-70'
        )}
        disabled={loading}
      >
        <div className="flex items-center gap-2 overflow-hidden">
          <svg
            className="h-5 w-5 flex-shrink-0 text-gray-500 dark:text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
            />
          </svg>
          <span className="truncate">
            {loading ? 'Loading...' : displayName}
          </span>
        </div>
        <svg
          className={cn(
            'h-5 w-5 flex-shrink-0 stroke-current transition-transform duration-200 ease-in-out',
            isOpen && 'rotate-180'
          )}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M4.79199 7.396L10.0003 12.6043L15.2087 7.396"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      <Dropdown
        isOpen={isOpen}
        onClose={closeDropdown}
        className="left-0 top-full z-50 mt-2 w-full min-w-[260px] max-w-sm rounded-xl border border-gray-200 bg-white p-3 shadow-theme-lg dark:border-gray-800 dark:bg-[#1E2635]"
      >
        {loading ? (
          <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
            Loading workspaces...
          </div>
        ) : workspaces.length === 0 ? (
          <div className="space-y-3">
            <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
              No workspaces available
            </div>
            <hr className="border-gray-200 dark:border-gray-700" />
            <Link
              to="/workspaces"
              className="flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium text-brand-600 hover:bg-brand-50 dark:text-brand-400 dark:hover:bg-brand-900/20"
              onClick={closeDropdown}
            >
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
              Create Workspace
            </Link>
          </div>
        ) : (
          <ul className="flex flex-col gap-1">
            {/* All Workspaces option */}
            <li>
              <DropdownItem
                onClick={handleAllWorkspacesSelect}
                onItemClick={closeDropdown}
                baseClassName=""
                className={cn(
                  'flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm font-medium transition-colors',
                  currentWorkspaceId === null
                    ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/20 dark:text-brand-400'
                    : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/5'
                )}
              >
                <div className="flex flex-col overflow-hidden">
                  <span>All Workspaces</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {totalFilings} {totalFilings === 1 ? 'filing' : 'filings'} total
                  </span>
                </div>
                {currentWorkspaceId === null && (
                  <svg
                    className="h-5 w-5 text-brand-600 dark:text-brand-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                )}
              </DropdownItem>
            </li>

            {/* Divider */}
            <li>
              <hr className="border-gray-200 dark:border-gray-700" />
            </li>

            {/* Workspace list */}
            {workspaces.map((workspace) => (
              <li key={workspace.id}>
                <DropdownItem
                  onClick={() => handleWorkspaceSelect(workspace)}
                  onItemClick={closeDropdown}
                  baseClassName=""
                  className={cn(
                    'flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm transition-colors',
                    workspace.id === currentWorkspaceId
                      ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/20 dark:text-brand-400'
                      : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/5'
                  )}
                >
                  <div className="flex flex-col overflow-hidden">
                    <span className="truncate font-medium">{workspace.name}</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {workspace.filing_count} {workspace.filing_count === 1 ? 'filing' : 'filings'}
                    </span>
                  </div>
                  {workspace.id === currentWorkspaceId && (
                    <svg
                      className="h-5 w-5 flex-shrink-0 text-brand-600 dark:text-brand-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  )}
                </DropdownItem>
              </li>
            ))}

            {/* Divider */}
            <li>
              <hr className="border-gray-200 dark:border-gray-700" />
            </li>

            {/* Manage Workspaces link */}
            <li>
              <Link
                to="/workspaces"
                className="flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/5"
                onClick={closeDropdown}
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                Manage Workspaces
              </Link>
            </li>
          </ul>
        )}
      </Dropdown>
    </div>
  );
};
